// Offline first sync (spec L05): changes go to the local database and the outbox at once,
// the outbox is pushed when there is a connection, and the server pokes over SSE when something changed.

import { useSyncExternalStore } from "react";
import { SYNC_TABLES, type MutationArgs, type MutationName, type MutationResult, type PullResponse } from "@shared/sync";
import { api, deviceId, isNetworkError } from "./api";
import { db, getMeta, setMeta } from "./db";

export interface SyncState {
  online: boolean;
  pending: number;
  syncing: boolean;
  lastSyncedAt: string | null;
}

type Listener = () => void;

class SyncEngine {
  private state: SyncState = {
    online: typeof navigator === "undefined" ? true : navigator.onLine,
    pending: 0,
    syncing: false,
    lastSyncedAt: null,
  };
  private listeners = new Set<Listener>();
  private events: EventSource | null = null;
  private running: Promise<void> | null = null;
  private again = false;
  private started = false;
  private onAuthLost: (() => void) | null = null;
  /** Raised by stop(): a round that is still running writes nothing more (logout must leave nothing behind). */
  private generation = 0;

  getState = (): SyncState => this.state;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private set(patch: Partial<SyncState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  /** Called with the reason when the server refuses a change (it is undone on the device after the pull). */
  onRefused: ((reason: string) => void) | null = null;

  async start(onAuthLost: () => void): Promise<void> {
    this.onAuthLost = onAuthLost;
    if (this.started) return;
    this.started = true;
    this.set({ pending: await db.outbox.count(), lastSyncedAt: (await getMeta<string>("lastSyncedAt")) ?? null });
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
    document.addEventListener("visibilitychange", this.handleVisible);
    this.openEvents();
    void this.sync();
  }

  stop(): void {
    this.started = false;
    this.generation++;
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
    document.removeEventListener("visibilitychange", this.handleVisible);
    this.events?.close();
    this.events = null;
  }

  /** Stops and waits until a round that was running has given up, so the local data can be cleared safely. */
  async halt(): Promise<void> {
    this.stop();
    await this.running?.catch(() => {});
  }

  private handleOnline = () => {
    this.set({ online: true });
    this.openEvents();
    void this.sync();
  };

  private handleOffline = () => this.set({ online: false });

  private handleVisible = () => {
    if (document.visibilityState === "visible") void this.sync();
  };

  private openEvents() {
    if (this.events || typeof EventSource === "undefined") return;
    const source = new EventSource("/api/sync/events");
    source.addEventListener("poke", () => void this.sync());
    // After a dropped connection the browser reconnects by itself; "ready" then means: catch up.
    source.addEventListener("ready", () => void this.sync());
    this.events = source;
  }

  /** Applies a change locally and queues it for the server. */
  async mutate<N extends MutationName>(name: N, args: MutationArgs<N>, rowIds: string[], apply: () => Promise<void>): Promise<void> {
    await db.transaction("rw", [...SYNC_TABLES.map((t) => db.syncTable(t)), db.outbox], async () => {
      await apply();
      await db.outbox.add({ id: crypto.randomUUID(), name, args, rowIds, createdAt: new Date().toISOString() });
    });
    this.set({ pending: await db.outbox.count() });
    void this.sync();
  }

  /** Push the outbox, then pull until up to date. Concurrent calls are merged into one more run. */
  sync(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    const generation = this.generation;
    this.running = (async () => {
      this.set({ syncing: true });
      try {
        do {
          this.again = false;
          await this.push();
          await this.pull(generation);
        } while (this.again && generation === this.generation);
        if (generation !== this.generation) return;
        const now = new Date().toISOString();
        await setMeta("lastSyncedAt", now);
        this.set({ online: true, lastSyncedAt: now });
      } catch (error) {
        if (isNetworkError(error)) this.set({ online: false });
        else if ((error as { status?: number }).status === 401) this.onAuthLost?.();
        else console.error("Sync failed", error);
      } finally {
        this.set({ syncing: false, pending: await db.outbox.count() });
        this.running = null;
        // A change that came in after the last round (while finishing up) still needs a round of its own.
        if (this.again && generation === this.generation) {
          this.again = false;
          void this.sync();
        }
      }
    })();
    return this.running;
  }

  private async push(): Promise<void> {
    for (;;) {
      const batch = await db.outbox.orderBy("seq").limit(100).toArray();
      if (!batch.length) return;
      const { results } = await api<{ results: MutationResult[] }>("/sync/push", {
        body: { deviceId: await deviceId(), mutations: batch.map(({ id, name, args }) => ({ id, name, args })) },
      });
      const done = new Set(results.map((r) => r.id));
      const refused = new Set(results.filter((r) => !r.ok).map((r) => r.id));
      for (const r of results) if (!r.ok) this.onRefused?.(r.error);
      // Refused mutations are dropped too. Their optimistic rows may not exist on the server (or differ):
      // forget those rows here and read everything again from the start, so the device matches the server (review B1).
      const stale = batch.filter((b) => refused.has(b.id)).flatMap((b) => b.rowIds);
      await db.transaction("rw", [...SYNC_TABLES.map((t) => db.syncTable(t)), db.outbox, db.meta], async () => {
        await db.outbox.bulkDelete(batch.filter((b) => done.has(b.id)).map((b) => b.seq!));
        if (stale.length) {
          const stillBusy = new Set((await db.outbox.toArray()).flatMap((o) => o.rowIds));
          const forget = stale.filter((id) => !stillBusy.has(id));
          for (const table of SYNC_TABLES) await db.syncTable(table).bulkDelete(forget);
          await setMeta("cursor", 0);
        }
      });
      if (stale.length) this.again = true;
      this.set({ pending: await db.outbox.count() });
    }
  }

  private async pull(generation: number): Promise<void> {
    // An older app version skipped rows of tables it did not know yet but still moved the cursor past them.
    // When this version syncs more tables than the last one, read everything again from the start.
    const tables = SYNC_TABLES.join(",");
    if ((await getMeta<string>("tables")) !== tables) {
      await setMeta("cursor", 0);
      await setMeta("tables", tables);
    }
    for (;;) {
      const since = (await getMeta<number>("cursor")) ?? 0;
      const page = await api<PullResponse>(`/sync/pull?since=${since}`);
      // Stopped meanwhile (logout): this page is not written.
      if (generation !== this.generation) return;
      let skipped = 0;
      await db.transaction("rw", [...SYNC_TABLES.map((t) => db.syncTable(t)), db.outbox, db.meta], async () => {
        if (generation !== this.generation) return;
        // Rows with a change still in the outbox keep their local state until that change is sent.
        const busy = new Set((await db.outbox.toArray()).flatMap((o) => o.rowIds));
        for (const table of SYNC_TABLES) {
          const allUpserts = page.upserts[table] ?? [];
          const allRemovals = page.removals[table] ?? [];
          const upserts = allUpserts.filter((r) => !busy.has(r.id as string));
          const removals = allRemovals.filter((id) => !busy.has(id));
          skipped += allUpserts.length - upserts.length + allRemovals.length - removals.length;
          if (upserts.length) await db.syncTable(table).bulkPut(upserts as never[]);
          if (removals.length) await db.syncTable(table).bulkDelete(removals);
        }
        // Skipped rows: keep the cursor, push first and read this page again.
        if (!skipped) await setMeta("cursor", page.cursor);
      });
      if (skipped) {
        this.again = true;
        return;
      }
      if (!page.more) return;
    }
  }
}

export const sync = new SyncEngine();

export function useSyncState(): SyncState {
  return useSyncExternalStore(sync.subscribe, sync.getState, sync.getState);
}
